/**
 * Sentry environments Tunasong reports under.
 *
 * `Sentry.init` previously set no `environment`, and the browser SDK defaults
 * to `production` when the field is absent — so PR previews reported as
 * production and polluted issue counts, `firstSeen`, and impact ranking
 * (GH #1732).
 */
export type SentryEnvironment = 'production' | 'preview' | 'development'

const productionHosts = ['tunasong.com', 'www.tunasong.com']
const previewHost = 'preview.tunasong.com'

/**
 * Classify a hostname into the environment its events belong to.
 *
 * Resolved at runtime rather than baked in at build time on purpose: CI builds
 * the app ONCE and serves the same artifact from both the preview bucket and
 * production (`.github/workflows/prod.yml`), so a compile-time value could only
 * ever be correct for one of the two origins.
 *
 * Matching is on whole labels — a plain `includes`/`endsWith` on the bare
 * domain would classify `tunasong.com.evil.test` as production.
 */
export const getSentryEnvironment = (hostname: string): SentryEnvironment => {
  if (productionHosts.includes(hostname)) {
    return 'production'
  }
  if (hostname === previewHost || hostname.endsWith(`.${previewHost}`)) {
    return 'preview'
  }
  return 'development'
}
